<!--
  Jeepay 通用表格, 支持基础分页， 检索

  @author terrfly

  @date 2021/5/8 07:18
-->
<template>
  <div class="jeepay-table-container" ref="tableContainer">
    <!-- 自定义吸附表头，在滚动时显示 -->
    <div class="sticky-header" v-show="showStickyHeader" ref="stickyHeader">
      <div class="sticky-header-content" :style="{ transform: `translateX(-${scrollLeft}px)` }">
        <table class="custom-header-table">
          <thead>
            <tr>
              <th
                v-for="(column, index) in alignedTableColumns"
                :key="index"
                :width="column.width || '200'"
                :style="{
                  textAlign: column.align || 'center',
                  ...(column.fixed ? {
                    position: 'sticky',
                    [column.fixed === 'left' || column.fixed === true ? 'left' : 'right']: column.fixed === 'left' || column.fixed === true ? getColumnLeftPosition(index) : 0,
                    zIndex: 2
                  } : {})
                }"
                :class="[
                  column.fixed === 'left' || column.fixed === true ? 'sticky-left' : '',
                  column.fixed === 'right' ? 'sticky-right' : ''
                ]"
              >
                <template v-if="column.slots && column.slots.title">
                  <slot :name="column.slots.title"></slot>
                </template>
                <template v-else>
                  {{ column.title }}
                </template>
              </th>
            </tr>
          </thead>
        </table>
      </div>
    </div>
    <a-table
      ref="innerTable"
      :columns="alignedTableColumns"
      :data-source="apiResData.records"
      :pagination="pagination"
      :loading="showLoading"
      @change="handleTableChange"
      :row-selection="rowSelection"
      :rowKey="rowKey"
      :default-align="'center'"
      :scroll="{ x: scrollX }"
      :customRow="(record, index) => {
        if(!tableRowCrossColor){
          return {};
        }
        return { style: { 'background-color': index % 2 == 0 ? '#FCFCFC' : '#FFFFFF'} }
      }"
    >
      <!-- 自定义列插槽， 参考：https://github.com/feseed/admin-antd-vue/blob/master/src/components/ShTable.vue  -->
      <!--  eslint-disable-next-line -->
      <template v-for="colCustom in columnsCustomSlots" :slot="colCustom.customRender" slot-scope="record">
        <slot :name="colCustom.customRender" :record="record"></slot>
      </template>

      <!-- 自定义表头title插槽 -->
      <!--  eslint-disable-next-line -->
      <template v-for="titleSlot in columnsTitleSlots" :slot="titleSlot.title">
        <slot :name="titleSlot.title"></slot>
      </template>
    </a-table>
  </div>
</template>
<script>

export default {

  name: 'JeepayTable', // 定义组件名称

  // 传递数据参数 ( 父-->子 参数 )
  props: {
    initData: { type: Boolean, default: true }, // 初始化列表数据， 默认true
    tableColumns: { type: Array, default: () => [] }, // 表格数组列
    reqTableDataFunc: { type: Function, default: () => {} }, // 请求列表数据
    currentChange: { type: Function, default: (v1, v2) => {} }, // 更新当前选择行事件， 默认空函数
    searchData: { type: Object, default: () => {} }, // 搜索条件参数
    pageSize: { type: Number, default: 10 }, // 默认每页条数
    rowSelection: { type: Object, default: () => {} }, // checkbox选择
    rowKey: { type: [String, Function], default: () => {} }, // 定义rowKey 如果不定义将会出现（树状结构出问题， checkbox不消失等）
    scrollX: { type: Number, default: 500 }, // 表格显示滚动条的宽度
    tableRowCrossColor: { type: Boolean, default: false }, // 是隔行换色
    enableStickyHeader: { type: Boolean, default: true } // 是否启用吸附表头
  },

  data () {
    return {
      apiResData: { total: 0, records: [] }, // 接口返回数据
      iPage: { pageNumber: 1, pageSize: this.pageSize }, // 默认table 分页/排序请求后端格式
      pagination: {
        total: 0,
        current: 1,
        pageSize: this.pageSize,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100', '300', '500', '1000'],
        showTotal: total => `共${total}条`
      }, // ATable 分页配置项
      showLoading: false,
      showStickyHeader: false, // 是否显示吸附表头
      headerHeight: 0, // 表头高度
      tableOffsetTop: 0, // 表格距离顶部的偏移量
      columnWidths: [], // 存储列宽度
      scrollLeft: 0, // 水平滚动位置
      scrollListener: false // 监听水平滚动标志
    }
  },
  // 计算属性
  computed: {
    columnsCustomSlots () { // 自定义列插槽  1. 过滤器仅获取到包含slot属性的元素， 2. 返回slot数组
      return this.tableColumns.filter(item => item.scopedSlots).map(item => item.scopedSlots)
    },
    columnsTitleSlots () { // 自定义表头title插槽
      return this.tableColumns.filter(item => item.slots && item.slots.title).map(item => item.slots)
    },
    // 统一设置列居中对齐
    alignedTableColumns () {
      return this.tableColumns.map(column => ({
        ...column,
        align: column.align || 'center' // 如果没有设置align，默认居中对齐
      }))
    }
  },
  mounted () {
    if (this.initData) { // 是否自动加载数据
      this.refTable(true)
    }
    // 调试title slots配置
    console.log('Table columns:', this.tableColumns)
    console.log('Title slots:', this.columnsTitleSlots)

    // 添加滚动事件监听
    if (this.enableStickyHeader) {
      this.$nextTick(() => {
        // 获取表格距离顶部的偏移量
        this.tableOffsetTop = this.getOffsetTop(this.$refs.tableContainer)

        // 监听滚动事件
        window.addEventListener('scroll', this.handleScroll)
      })
    }
  },
  beforeDestroy () {
    // 移除滚动事件监听
    if (this.enableStickyHeader) {
      window.removeEventListener('scroll', this.handleScroll)
    }
  },
  methods: {
    handleTableChange (pagination, filters, sorter) { // 分页、排序、筛选变化时触发
      this.pagination = pagination
      this.iPage = {
        pageSize: pagination.pageSize, // 每页条数
        pageNumber: pagination.current, // 当前页码
        sortField: sorter.columnKey, // 排序字段
        // sortOrder: sorter.order, // 排序顺序
        isAsc: sorter.columnKey && sorter.order === 'ascend',
        ...filters // 过滤数据
      }
      this.refTable()
    },

    // 查询数据
    refTable (isToFirst = false) {
      const that = this
      if (isToFirst) {
        this.iPage.pageNumber = 1
        this.pagination.current = 1
      }
      // 更新检索数据
      this.showLoading = true
      this.reqTableDataFunc(Object.assign({}, this.iPage, this.searchData)).then(resData => {
        this.pagination.total = resData.total // 更新总数量
        this.apiResData = resData // 列表数据更新
        this.showLoading = false // 关闭loading

        // 数据为0 ，并且为当前页面没有在第一页则需要自动跳转到上一页（解决，删除第二页数据全部删除后无数据的情况 ）
        if (resData.records.length === 0 && this.iPage.pageNumber > 1) {
          that.$nextTick(() => {
            // 最大页码
            const maxPageNumber = (resData.total / this.iPage.pageSize) + ((resData.total % this.iPage.pageSize) === 0 ? 0 : 1)
            if (maxPageNumber === 0) { // 已经无数据
              return false
            }

            // 跳转到的页码
            const toPageSize = (this.iPage.pageNumber - 1) > maxPageNumber ? maxPageNumber : (this.iPage.pageNumber - 1)

            this.iPage.pageNumber = toPageSize
            this.pagination.current = toPageSize
            that.refTable(false)
          })
        }
        // 请求成功后，关闭查询按钮的loading
        that.$emit('btnLoadClose')
      }).catch(res => {
        this.showLoading = false
        that.$emit('btnLoadClose')
      }) // 关闭loading
    },
    // 计算元素距离页面顶部的偏移量
    getOffsetTop (el) {
      let offsetTop = 0
      while (el && el !== document.body) {
        offsetTop += el.offsetTop
        el = el.offsetParent
      }
      return offsetTop
    },
    // 处理滚动事件
    handleScroll () {
      console.log('处理滚动事件')
      if (!this.enableStickyHeader) return

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop

      // 获取表头元素
      const tableHeader = this.$refs.innerTable.$el.querySelector('.ant-table-thead')
      if (!tableHeader) return

      // 计算表头的高度（如果还没有计算过）
      if (this.headerHeight === 0) {
        this.headerHeight = tableHeader.offsetHeight
      }

      // 计算表头的位置
      const headerPosition = this.tableOffsetTop

      // 当滚动超过表头位置时，显示吸附表头
      if (scrollTop > headerPosition) {
        // 同步吸附表头的宽度和原表头的宽度
        if (this.$refs.stickyHeader) {
          const headerColumns = tableHeader.querySelectorAll('th')
          const stickyColumns = this.$refs.stickyHeader.querySelectorAll('th')

          // 存储每列的宽度
          this.columnWidths = []

          if (headerColumns.length === stickyColumns.length) {
            headerColumns.forEach((col, index) => {
              // 存储列宽度
              this.columnWidths[index] = col.offsetWidth

              if (stickyColumns[index]) {
                stickyColumns[index].style.width = col.offsetWidth + 'px'
              }
            })
          }

          // 设置吸附表头的位置
          this.$refs.stickyHeader.style.top = '0'
          this.$refs.stickyHeader.style.width = this.$refs.innerTable.$el.querySelector('.ant-table-content').offsetWidth + 'px'

          // 监听水平滚动
          const tableContent = this.$refs.innerTable.$el.querySelector('.ant-table-content')
          if (tableContent) {
            this.scrollLeft = tableContent.scrollLeft
            if (!this.scrollListener) {
              this.scrollListener = true
              tableContent.addEventListener('scroll', this.handleHorizontalScroll)
            }
          }
        }

        this.showStickyHeader = true
      } else {
        this.showStickyHeader = false

        // 移除水平滚动监听
        if (this.scrollListener) {
          const tableContent = this.$refs.innerTable.$el.querySelector('.ant-table-content')
          if (tableContent) {
            tableContent.removeEventListener('scroll', this.handleHorizontalScroll)
          }
          this.scrollListener = false
        }
      }
    },

    // 处理水平滚动
    handleHorizontalScroll (e) {
      if (this.$refs.stickyHeader) {
        this.scrollLeft = e.target.scrollLeft

        // 更新固定列的位置
        const headerColumns = this.$refs.innerTable.$el.querySelectorAll('.ant-table-thead th')
        const stickyColumns = this.$refs.stickyHeader.querySelectorAll('th')

        if (headerColumns.length === stickyColumns.length) {
          headerColumns.forEach((col, index) => {
            const column = this.tableColumns[index]
            if (column && (column.fixed === 'left' || column.fixed === true)) {
              if (stickyColumns[index]) {
                stickyColumns[index].style.left = this.getColumnLeftPosition(index)
              }
            }
          })
        }
      }
    },

    // 计算左侧固定列的位置
    getColumnLeftPosition (index) {
      let leftPos = 0
      for (let i = 0; i < index; i++) {
        leftPos += this.columnWidths[i] || 0
      }
      return leftPos + 'px'
    }
  }
}
</script>
<style lang="less">

// 调整antdv 的table默认padding高度
.ant-table-fixed{
  tr{
    th{
      padding: 8px 8px !important;
    }
    th:first-child{ // 第一个表格 左填充16， 其他为8
      padding-left: 16px !important;
    }
    td{
      padding: 8px 8px !important;
    }
    td:first-child{
      padding-left: 16px !important;
    }
  }
}
.jeepay-table-container {
  position: relative;

  .sticky-header {
    position: fixed;
    top: 0;
    left: 220px;
    z-index: 1000;
    background-color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    transition: opacity 0.3s;

    .sticky-header-content {
      width: 100%;
      overflow: hidden;
      overflow-x: auto;
      // 滚动条样式调整，变细
      &::-webkit-scrollbar {
        width: 3px;
        height: 3px;
      }
      &::-webkit-scrollbar-track {
        background-color: #e8e8e8;
      }
      &::-webkit-scrollbar-thumb {
        background-color: #e8e8e8;
        border-radius: 3px;
      }
    }

    .custom-header-table {
      width: 100%;
      table-layout: fixed;
      border-collapse: separate;
      border-spacing: 0;
      position: relative; /* 为固定列提供定位上下文 */

      thead {
        display: table-header-group;
        vertical-align: middle;
        border-color: inherit;
      }

      tr {
        display: table-row;
        vertical-align: inherit;
        border-color: inherit;
        position: relative; /* 为固定列提供定位上下文 */
      }

      th {
        position: relative;
        background-color: #fafafa;
        transition: background 0.3s ease;
        font-weight: 500;
        text-align: left;
        color: rgba(0, 0, 0, 0.85);
        padding: 8px 8px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        border-bottom: 1px solid #f0f0f0;
        font-size: 14px;
        line-height: 1.5;
        word-break: break-word;

        &.sticky-left {
          background-color: #fafafa;
          z-index: 2;
          border-right: 1px solid #f0f0f0;
          /* 左侧固定列需要与前一列接壤 */
          &:after {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            width: 30px;
            transform: translateX(100%);
            transition: box-shadow .3s;
            pointer-events: none;
            z-index: 1;
          }
        }

        &.sticky-right {
          background-color: #fafafa;
          z-index: 2;
          border-left: 1px solid #f0f0f0;
          /* 右侧固定列需要与前一列接壤 */
          &:after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            width: 30px;
            transform: translateX(-100%);
            transition: box-shadow .3s;
            pointer-events: none;
            z-index: 1;
          }
        }
      }

      th:first-child {
        padding-left: 16px !important;
      }
    }
  }
}

</style>
